import { defaultSocketFactory, SOCKET_OPEN, } from "./socket.js";
/** Application close code meaning the credential was rejected. */
const UNAUTHORISED = 4401;
const MAX_BACKOFF_MS = 30_000;
/**
 * Names duplicated from the services rather than imported.
 *
 * The packages that own them are Zod-based server packages, and pulling a
 * validation library into a bundle that loads on every product page buys nothing.
 * These strings are part of a wire contract and change about as often as the wire.
 */
const AUDIENCE_QUERY_PARAM = "audience";
const AUTH_SUBPROTOCOL_PREFIX = "island.auth.";
/**
 * The companion subprotocol a credential-bearing client must also offer.
 *
 * There is a sharp edge in the WebSocket specification here. A server may not select
 * a subprotocol the client did not offer, and a client that offered some and
 * receives none back fails its own handshake. The server will not echo the
 * token-bearing value — that would put a credential in a response header, logged by
 * every proxy in between — so it selects this one instead. Offering it is not
 * optional: without it the connection dies at the handshake with a message about a
 * missing subprotocol that says nothing about the real cause.
 */
export const WS_SUBPROTOCOL = "island.v1";
/**
 * The socket URL, with the audience appended on the cookie path.
 *
 * Built with `URL` rather than string concatenation so a configured URL that
 * already carries a query string keeps it. Nothing is appended on the bearer path:
 * the token identifies the principal, and the service derives the audience from it.
 */
function socketUrl(url, auth) {
    if (auth.mode === "bearer")
        return url;
    try {
        const parsed = new URL(url);
        parsed.searchParams.set(AUDIENCE_QUERY_PARAM, auth.audience);
        return parsed.toString();
    }
    catch {
        // A URL the platform cannot parse is one the WebSocket constructor will reject
        // anyway. Passing it through unchanged keeps the failure where it belongs.
        return url;
    }
}
export function createRealtimeClient(options) {
    const createSocket = options.createSocket ?? defaultSocketFactory;
    let socket = null;
    let state = "closed";
    let attempt = 0;
    let reconnectTimer = null;
    let closedByCaller = false;
    /**
     * Set when the server has told us when to come back.
     *
     * The close that follows a `going_away` must not schedule a second reconnect: it
     * would overwrite the delay the server asked for with a much shorter backoff, and
     * the herd the delay exists to prevent arrives anyway.
     */
    let reconnectScheduledByServer = false;
    /**
     * Conversations this client wants to watch.
     *
     * Kept separately from the socket so they can be re-established after a
     * reconnect. A client that lost its subscriptions on every deploy would show a
     * silent, stale transcript — which is worse than an obvious disconnection.
     */
    const desiredSubscriptions = new Set();
    /** Frames queued because `ready` has not arrived yet. */
    const pending = [];
    function setState(next) {
        state = next;
        options.onStateChange?.(next);
    }
    function send(frame) {
        if (socket?.readyState === SOCKET_OPEN && state === "open") {
            socket.send(JSON.stringify(frame));
            return;
        }
        pending.push(frame);
    }
    function flushPending() {
        for (const conversationId of desiredSubscriptions) {
            socket?.send(JSON.stringify({ type: "subscribe", conversationId }));
        }
        while (pending.length > 0) {
            const frame = pending.shift();
            if (frame !== undefined)
                socket?.send(JSON.stringify(frame));
        }
    }
    /**
     * Exponential backoff with jitter.
     *
     * The jitter is not decoration: without it every client in the estate reconnects
     * on the same millisecond after a deploy, and the instance that just came up
     * absorbs all of them at once.
     */
    function backoffMs() {
        const base = Math.min(2 ** attempt * 500, MAX_BACKOFF_MS);
        return base + Math.floor(Math.random() * 500);
    }
    function scheduleReconnect(delayMs) {
        if (closedByCaller)
            return;
        setState("reconnecting");
        reconnectTimer = setTimeout(connect, delayMs);
    }
    /**
     * The subprotocols to offer, or `null` if the client cannot authenticate at all.
     *
     * `null` is distinct from "no subprotocols": on the bearer path a missing token
     * means there is nothing to connect with, and opening the socket anyway would
     * spend a handshake to be told 4401 — which then stops reconnection permanently.
     */
    function protocolsFor(auth) {
        if (auth.mode === "cookie")
            return undefined;
        const token = auth.accessToken();
        if (token === null || token.length === 0)
            return null;
        return [WS_SUBPROTOCOL, `${AUTH_SUBPROTOCOL_PREFIX}${token}`];
    }
    function connect() {
        if (closedByCaller)
            return;
        const protocols = protocolsFor(options.auth);
        if (protocols === null) {
            // No credential to present. Treated exactly as a refusal, because it is one:
            // the shell must sign in or refresh before a socket can be worth opening.
            closedByCaller = true;
            setState("closed");
            options.onUnauthorised?.();
            return;
        }
        reconnectScheduledByServer = false;
        setState(attempt === 0 ? "connecting" : "reconnecting");
        socket = createSocket(socketUrl(options.url, options.auth), protocols);
        socket.onmessage = (message) => {
            let frame;
            try {
                frame = JSON.parse(String(message.data));
            }
            catch {
                return;
            }
            switch (frame.type) {
                case "ready":
                    attempt = 0;
                    setState("open");
                    // Only now is it safe to send anything at all.
                    flushPending();
                    return;
                case "event":
                    options.onEvent(frame.event);
                    return;
                case "going_away":
                    // The server is being replaced. Wait as asked, jittered.
                    reconnectScheduledByServer = true;
                    attempt = 0;
                    scheduleReconnect(frame.reconnectAfterMs * (0.5 + Math.random()));
                    return;
                case "error":
                    // Reported, not fatal: a refused subscription must not tear down a socket
                    // that is serving other conversations correctly.
                    console.warn("realtime error frame", frame.code, frame.message);
                    return;
                case "subscribed":
                case "unsubscribed":
                case "pong":
                    return;
            }
        };
        socket.onclose = (event) => {
            if (event.code === UNAUTHORISED) {
                // Reconnecting would fail identically for ever. The session needs renewing
                // first, which is the shell's job.
                closedByCaller = true;
                setState("closed");
                options.onUnauthorised?.();
                return;
            }
            if (closedByCaller) {
                setState("closed");
                return;
            }
            // Already waiting on the delay the server named. Leave it alone.
            if (reconnectScheduledByServer)
                return;
            attempt += 1;
            scheduleReconnect(backoffMs());
        };
        socket.onerror = () => {
            // `onclose` always follows, and it is where reconnection is decided.
        };
    }
    return {
        connect() {
            closedByCaller = false;
            attempt = 0;
            connect();
        },
        subscribe(conversationId) {
            desiredSubscriptions.add(conversationId);
            send({ type: "subscribe", conversationId });
        },
        unsubscribe(conversationId) {
            desiredSubscriptions.delete(conversationId);
            send({ type: "unsubscribe", conversationId });
        },
        typing(conversationId, active) {
            send({ type: "typing", conversationId, active });
        },
        markSeen(conversationId) {
            send({ type: "seen", conversationId });
        },
        close() {
            closedByCaller = true;
            if (reconnectTimer !== null)
                clearTimeout(reconnectTimer);
            socket?.close();
            setState("closed");
        },
        get state() {
            return state;
        },
    };
}
//# sourceMappingURL=client.js.map